import { Currency } from '@sequencehq/api'
import {
  CheckboxLineInput,
  DatePickerField,
  Flex,
  Grid,
  InputSelectControlField,
  PriceField,
  SimpleModal,
  TextInputField
} from '@sequencehq/core-components'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { useEnabledCurrencies } from 'SettingsV2/index'
import {
  DATE_FORMAT,
  EXPIRY_OPTIONS
} from './createCustomerCreditGrant.constants'
import { Expiry } from './createCustomerCreditGrant.validation'
import { useCreateCustomerCreditGrantForm } from './useCreateCustomerCreditGrantForm'

/**
 * Creates a V2 customer credit grant: a one-off pot of cash the customer can
 * spend on any invoice, held outside any billing schedule. Mount it only while
 * open, so each open starts a fresh form and idempotency key.
 */
export const CreateCustomerCreditGrantDrawer = ({
  customerId,
  onClose
}: {
  customerId: string
  onClose: () => void
}) => {
  const enabledCurrencies = useEnabledCurrencies({ toastOnError: true })

  if (enabledCurrencies.status !== 'SUCCESS') {
    return null
  }

  return (
    <CreateCustomerCreditGrantForm
      customerId={customerId}
      enabledCurrencies={enabledCurrencies.enabledCurrencies}
      onClose={onClose}
    />
  )
}

const CreateCustomerCreditGrantForm = ({
  customerId,
  enabledCurrencies,
  onClose
}: {
  customerId: string
  enabledCurrencies: Currency[]
  onClose: () => void
}) => {
  const { fields, onSubmit } = useCreateCustomerCreditGrantForm({
    customerId,
    enabledCurrencies
  })

  const hasCurrencyChoice = enabledCurrencies.length > 1

  return (
    <SimpleModal
      isOpen
      onClose={onClose}
      variant="drawer"
      title="Add customer credit"
      submitButtonText="Add credit"
      submittingButtonText="Adding credit..."
      onSubmit={onSubmit}
      data-testid="createCustomerCreditGrant.drawer"
    >
      <Flex flexDirection="column" gap="16px">
        {hasCurrencyChoice && (
          <InputSelectControlField
            label="Currency"
            options={enabledCurrencies.map(currency => ({
              label: currency,
              value: currency
            }))}
            value={fields.currency.value}
            onChange={value => fields.currency.onChange(value as Currency)}
            validationErrors={fields.currency.validationErrors}
            data-testid="createCustomerCreditGrant.currency"
          />
        )}
        <Flex flexDirection="column" gap="8px">
          <PriceField
            label="Credit amount"
            variant="v2"
            currency={fields.currency.value}
            value={fields.amount.value}
            onChange={valueAsString => fields.amount.onChange(valueAsString)}
            validationErrors={fields.amount.validationErrors}
            data-testid="createCustomerCreditGrant.amount"
          />
          <CheckboxLineInput
            text="Charge for this credit"
            description="Creates a draft invoice to charge this customer for the credits"
            value={fields.isCharged.value}
            onChange={fields.isCharged.onChange}
            data-testid="createCustomerCreditGrant.isCharged"
          />
        </Flex>
        {fields.isCharged.value && (
          <PriceField
            label="Price charged"
            variant="v2"
            currency={fields.currency.value}
            value={fields.costOfCredit.value}
            onChange={valueAsString =>
              fields.costOfCredit.onChange(valueAsString)
            }
            validationErrors={fields.costOfCredit.validationErrors}
            data-testid="createCustomerCreditGrant.costOfCredit"
          />
        )}
        <Grid templateColumns="1fr 1fr" gap="16px">
          <DatePickerField
            label="Effective date"
            width="100%"
            dateFormat={DATE_FORMAT}
            value={apiDatesAdapters.fromApi(fields.effectiveDate.value)}
            onChange={date =>
              fields.effectiveDate.onChange(apiDatesAdapters.toApi(date))
            }
            validationErrors={fields.effectiveDate.validationErrors}
            data-testid="createCustomerCreditGrant.effectiveDate"
          />
          <InputSelectControlField
            label="Expiry"
            options={EXPIRY_OPTIONS}
            value={fields.expiry.value}
            onChange={value => fields.expiry.onChange(value as Expiry)}
            validationErrors={fields.expiry.validationErrors}
            data-testid="createCustomerCreditGrant.expiry"
          />
        </Grid>
        {fields.expiry.value === 'ON_DATE' && (
          <DatePickerField
            label="Expires on"
            width="100%"
            dateFormat={DATE_FORMAT}
            placeholder="Select date"
            value={apiDatesAdapters.fromApi(fields.expiryDate.value)}
            onChange={date =>
              fields.expiryDate.onChange(apiDatesAdapters.toApi(date))
            }
            validationErrors={fields.expiryDate.validationErrors}
            data-testid="createCustomerCreditGrant.expiryDate"
          />
        )}
        <TextInputField
          label="Display name (optional)"
          placeholder="Name"
          description="This is the name that gets displayed on invoice"
          value={fields.name.value}
          onChange={e => fields.name.onChange(e.target.value)}
          validationErrors={fields.name.validationErrors}
          data-testid="createCustomerCreditGrant.name"
        />
      </Flex>
    </SimpleModal>
  )
}
